import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router';
import { isRttsEnabled, isRttsUseMock } from '../../config/featureFlags';
import { LscAuthGate } from '../../lsc/hooks/useLscAuthGate';
import { useLeague } from '../../lsc/hooks/useLeague';
import ErrorBanner from '../../lsc/components/ErrorBanner';
import LoadingState from '../../lsc/components/LoadingState';
import { listLscArchive, LscApiError } from '../../lsc/api';
import { lscReturnTo } from '../../lsc/routing';
import { configureRttsMock, listRttsGames, resetRttsMock } from './api';
import GameRow from './components/GameRow';
import { gateFieldsFromArchive } from './lib/normalizeGateFields';
import type { RttsGateFields, RttsUserRole } from './types';

/**
 * Dedicated RTTS tab — entry point #2.
 * Live: maps gate fields from the league archive list (PR #143).
 * Mock: fixture games via listRttsGames.
 */
const RttsTab: React.FC = () => {
  const { leagueId } = useParams<{ leagueId: string }>();

  if (!isRttsEnabled()) {
    return (
      <div className="space-y-2" data-testid="rtts-tab-disabled">
        <h1 className="text-2xl font-bold text-white">Scoutlytics Studio</h1>
        <p className="text-sm text-secondary-400">This feature is not enabled.</p>
      </div>
    );
  }

  return (
    <LscAuthGate returnTo={lscReturnTo(`/leagues/${leagueId}/rtts`)}>
      <RttsTabContent />
    </LscAuthGate>
  );
};

function RttsTabContent() {
  const { leagueId } = useParams<{ leagueId: string }>();
  const { isCommissioner } = useLeague();
  const userRole: RttsUserRole = isCommissioner ? 'commissioner' : 'member';
  const [games, setGames] = useState<RttsGateFields[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mockEntitled, setMockEntitled] = useState(true);
  const useMock = isRttsUseMock();

  const load = useCallback(async () => {
    if (!leagueId) return;
    setLoading(true);
    setError(null);
    try {
      if (useMock) {
        setGames(await listRttsGames(leagueId));
      } else {
        const items = await listLscArchive(leagueId, {
          archive_status: 'published',
          limit: 100,
        });
        setGames(items.map((archive) => gateFieldsFromArchive(archive)));
      }
    } catch (err) {
      if (err instanceof LscApiError) {
        setError(err.message);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load games.');
      }
    } finally {
      setLoading(false);
    }
  }, [leagueId, useMock]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      resetRttsMock();
    };
  }, []);

  const onMockEntitlementToggle = (next: boolean) => {
    setMockEntitled(next);
    configureRttsMock({ entitled: next });
    void load();
  };

  if (loading) return <LoadingState message="Loading Studio…" />;

  return (
    <div className="space-y-6" data-testid="rtts-tab">
      <div>
        <h1 className="text-2xl font-bold text-white mb-1">Scoutlytics Studio</h1>
        <p className="text-sm text-secondary-400">
          Generate highlight reels from finished league games. Only the broadcaster can
          create a reel for their archive.
        </p>
      </div>

      {useMock ? (
        <div
          className="rounded-lg border border-secondary-700 bg-secondary-900/40 px-4 py-3 flex flex-wrap items-center gap-3"
          data-testid="rtts-mock-controls"
        >
          <span className="text-xs text-secondary-400">Mock controls</span>
          <label className="flex items-center gap-2 text-xs text-secondary-300">
            <input
              type="checkbox"
              checked={mockEntitled}
              onChange={(event) => onMockEntitlementToggle(event.target.checked)}
              data-testid="rtts-mock-entitled-toggle"
            />
            League entitled
          </label>
        </div>
      ) : null}

      {error ? <ErrorBanner message={error} /> : null}

      {games.length === 0 && !error ? (
        <p className="text-sm text-secondary-400" data-testid="rtts-tab-empty">
          No finished games available yet.
        </p>
      ) : (
        <div className="lsc-split-when-wide">
        <div className="lsc-split-grid" data-testid="rtts-game-list">
          {games.map((gates) => (
            <GameRow
              key={gates.archive_id}
              leagueId={leagueId!}
              gates={gates}
              userRole={userRole}
            />
          ))}
        </div>
        </div>
      )}
    </div>
  );
}

export default RttsTab;
